// The badge moment on the results screen: under the score, above PLAY AGAIN.
// The rarest badge this game earned is shown large with what the next tier
// asks for; any others sit beside it as chips that swap in when tapped.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { badgeArt, badgeSpec, earnedLine, nextStep, tierLabel, TIER_METAL, TIER_TEXT, type BadgeSync, type Tier } from '../lib/badges';
import { shareBadge } from '../lib/badgeShare';
import { SharePreview } from './SharePreview';

export function NewBadges({ sync, nickname }: { sync: BadgeSync | null; nickname: string }) {
  const [pick, setPick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ url: string; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  if (!sync || sync.fresh.length === 0) return null;

  const shown = sync.fresh[Math.min(pick, sync.fresh.length - 1)];
  const state = sync.badges.find((b) => b.id === shown.id)!;
  const spec = badgeSpec(shown.id);
  const tier = shown.tier as Tier;
  const label = tierLabel(spec, tier);
  const step = nextStep(state);
  const nextMetal = state.tier < 3 ? TIER_METAL[(state.tier + 1) as Tier] : TIER_METAL[3];

  async function onShare() {
    if (busy) return;
    setBusy(true);
    const done = await shareBadge({ ...state, tier }, nickname);
    setBusy(false);
    if (done.outcome === 'copied') { setCopied(true); setTimeout(() => setCopied(false), 2000); }
    if (done.previewUrl) setPreview({ url: done.previewUrl, text: done.text });
  }

  return (
    <div className="badge-new animate-in" role="group" aria-label={`New badge: ${spec.name}`}>
      {/* Portalled: the card animates in with a transform, and a fixed
          overlay inside a transformed box is pinned to the box, not the screen. */}
      {preview && createPortal(
        <SharePreview url={preview.url} text={preview.text} fileName="wot-badge.png" onClose={() => setPreview(null)} />,
        document.body,
      )}
      <span className="badge-new-label">{sync.fresh.length > 1 ? `NEW BADGES · ${sync.fresh.length}` : 'NEW BADGE'}</span>
      <div className="badge-new-main">
        <img key={`${shown.id}-${tier}`} className="badge-new-art" src={badgeArt(shown.id, tier)} alt="" width={92} height={92} />
        <div className="badge-new-text">
          <span className="badge-name">{spec.name}</span>
          {label && <span className="badge-tier" style={{ color: TIER_TEXT[tier] }}>{label}</span>}
          <span className="badge-line">{earnedLine(shown.id, tier)}</span>
        </div>
      </div>
      {step && (
        <div className="badge-progress">
          <div className="badge-progress-row"><span>{step.label}</span><span>{step.have} / {step.need}</span></div>
          <div className="badge-progress-bar"><i style={{ width: `${Math.max(4, (100 * step.have) / step.need)}%`, background: nextMetal }} /></div>
        </div>
      )}
      <div className="badge-new-foot">
        <div className="badge-chips">
          {sync.fresh.length > 1 && sync.fresh.map((b, i) => (
            <button key={b.id} className={`badge-chip ${i === pick ? 'on' : ''}`} onClick={() => setPick(i)}
              aria-pressed={i === pick} aria-label={badgeSpec(b.id).name}>
              <img src={badgeArt(b.id, b.tier)} alt="" width={24} height={24} />
              <span>{badgeSpec(b.id).name}</span>
            </button>
          ))}
        </div>
        <button className="badge-share-btn" onClick={onShare} disabled={busy}>{copied ? 'COPIED' : 'SHARE'}</button>
      </div>
    </div>
  );
}
